import { z } from 'zod';

import { TimeOffsetRelation, TimeOffsetUnit, GUEST_STATUSES } from 'constants/enums';
import { convertSendAtToDBValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import dayjs from 'utils/extendedDayJS';
import { GuestStatus } from 'types/guest.types';

import {
  getPostEventReminderWindowEnd,
  POST_EVENT_REMINDER_WINDOW_DAYS
} from '../../../utils/postEventReminderWindow';

// Map GUEST_STATUSES to GuestStatus enum for options
export const GUEST_STATUS_MAPPING = {
  [GUEST_STATUSES.ATTENDED]: GuestStatus.ATTENDED,
  [GUEST_STATUSES.CONFIRMED]: GuestStatus.CONFIRMED,
  [GUEST_STATUSES.INCOMPLETE]: 'incomplete',
  [GUEST_STATUSES.INVITED]: GuestStatus.INVITED,
  [GUEST_STATUSES.MAYBE]: 'maybe',
  [GUEST_STATUSES.CANCELLED]: GuestStatus.CANCELLED,
  [GUEST_STATUSES.PENDING]: 'pending',
  [GUEST_STATUSES.WAITLISTED]: GuestStatus.WAITLISTED
};

export const guestStatusOptions = Object.entries(GUEST_STATUS_MAPPING).map(([label, value]) => ({
  label,
  value
}));

/**
 * TODO: There's whole bunch need to be done to save/load the form values from the backend.
 * @param datetimeStart
 * @param datetimeEnd
 * @param eventTimezone
 * @returns
 */
const scheduleSchema = (
  datetimeStart: string,
  datetimeEnd: string,
  eventTimezone?: string,
  scheduledPublishAt?: string
) => {
  return z
    .object({
      /**
       * Relative time fields
       */
      quantity: z
        .number({ invalid_type_error: 'Send time is required' })
        .positive({ message: 'Send time must be a positive number' })
        .optional(),
      unit: z.nativeEnum(TimeOffsetUnit).optional(),

      // relation will be transformed to beforeEventBegins boolean for backend
      // True: before the event begins
      // False: after the event begins
      relation: z
        .nativeEnum(TimeOffsetRelation, {
          errorMap: () => ({ message: 'Relation is required' })
        })
        .optional(),

      /**
       * Absolute time field
       * Transform to DB value for backend.
       */
      sendAt: z
        .date()
        .transform((date: Date): string | undefined =>
          date ? convertSendAtToDBValue(date, eventTimezone) : undefined
        )
        .optional(),

      /**
       * Virtual field to indicate if using relative time scheduling or absolute time scheduling
       */
      isRelativeTime: z.boolean().optional()
    })
    .superRefine((data, ctx) => {
      /**
       * Relative time schema check
       */
      if (data.isRelativeTime) {
        if (data.quantity === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Quantity is required for relative time',
            path: ['quantity']
          });
        }
        if (data.unit === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Unit is required for relative time',
            path: ['unit']
          });
        }
        if (data.relation === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Relation is required for relative time',
            path: ['relation']
          });
        }
        if (data.quantity !== undefined && data.unit && data.relation !== undefined) {
          // Checking general schedule errors from relative time fields
          const calculatedSendAt = dayjs.subtractOrAddTimeFromDateTime(
            datetimeStart,
            data.quantity,
            data.unit,
            data.relation === TimeOffsetRelation.BEFORE,
            'UTC'
          );

          // Relative time must be in the future
          if (dayjs.utc(calculatedSendAt).isBefore(dayjs.utc())) {
            // This will be reported to '{formstate}.errors.schedule.root'
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Scheduled time must be in the future'
            });
          }

          // Relative time must be within the post-event reminder window
          const postEventReminderWindowEnd = getPostEventReminderWindowEnd(datetimeEnd);
          if (
            postEventReminderWindowEnd &&
            dayjs.utc(calculatedSendAt).isAfter(postEventReminderWindowEnd)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Scheduled time must be within ${POST_EVENT_REMINDER_WINDOW_DAYS} days after the event ends`
            });
          }

          // Relative time must be after scheduled publish time
          if (
            scheduledPublishAt &&
            dayjs.utc(calculatedSendAt).isBefore(dayjs.utc(scheduledPublishAt))
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Scheduled time must be after the event is scheduled for publishing',
              fatal: true
            });
          }
        }
      }

      /**
       * Absolute time schema check
       */
      if (!data.isRelativeTime) {
        if (data.sendAt === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Send time is required for absolute time',
            path: ['sendAt']
          });
        }

        // sentAt must be in the future
        if (data.sendAt && dayjs.utc(data.sendAt).isBefore(dayjs.utc())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Scheduled time must be in the future',
            path: ['sendAt']
          });
        }

        // sendAt must be within the post-event reminder window
        const postEventReminderWindowEnd = getPostEventReminderWindowEnd(datetimeEnd);
        if (
          postEventReminderWindowEnd &&
          data.sendAt &&
          dayjs.utc(data.sendAt).isAfter(postEventReminderWindowEnd)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Scheduled time must be within ${POST_EVENT_REMINDER_WINDOW_DAYS} days after the event ends`,
            path: ['sendAt']
          });
        }

        // sendAt must be after scheduled publish time
        if (
          scheduledPublishAt &&
          data.sendAt &&
          dayjs.utc(data.sendAt).isBefore(dayjs.utc(scheduledPublishAt))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Scheduled time must be after the event is scheduled for publishing',
            path: ['sendAt'],
            fatal: true
          });
        }
      }
    });
};

export const slackReminderFormSchema = (
  datetimeStart: string,
  datetimeEnd: string,
  eventTimezone?: string,
  scheduledPublishAt?: string
) =>
  z.object({
    title: z.string().min(1, 'Title is required'),
    message: z.string().min(1, 'Message is required'),
    selectedGuestStatuses: z.array(z.string()).min(1, 'Please select at least one guest status'),
    schedule: scheduleSchema(datetimeStart, datetimeEnd, eventTimezone, scheduledPublishAt)
  });

export const emailReminderFormSchema = (
  datetimeStart: string,
  datetimeEnd: string,
  eventTimezone?: string,
  scheduledPublishAt?: string
) =>
  z.object({
    subject: z.string().min(1, 'Subject is required'),
    message: z.string().min(1, 'Message is required'),
    selectedGuestStatuses: z.array(z.string()).min(1, 'Please select at least one guest status'),
    schedule: scheduleSchema(datetimeStart, datetimeEnd, eventTimezone, scheduledPublishAt)
  });
export type SlackReminderFormInput = z.input<ReturnType<typeof slackReminderFormSchema>>;
export type SlackReminderFormValues = z.infer<ReturnType<typeof slackReminderFormSchema>>;
export type EmailReminderFormInput = z.input<ReturnType<typeof emailReminderFormSchema>>;
export type EmailReminderFormValues = z.infer<ReturnType<typeof emailReminderFormSchema>>;
export type ScheduleFormValues = z.infer<ReturnType<typeof scheduleSchema>>;
